import { createContext, useContext } from 'react';

import type { ReelTranscriptCollapseView } from './useReelTranscriptCollapse';
import type {
  ReelTranscriptSection,
  ReelTranscriptStructure,
  ReelWordStamp,
  StructureTokenLike,
} from '@src/services/reel/reelTranscriptStructure';

/**
 * PLAT-11157 — how the reel's structure reaches the transcript's prose.
 *
 * The shared transcript component has four hosts and sits under a size and
 * prop-count ratchet, so the structure arrives as a context the clip preview
 * publishes rather than as a new prop. The default is `null`: a host that
 * publishes nothing — every host but the clip preview — renders exactly
 * today's transcript, with no reel code on its path.
 */
export type ReelTranscriptStructureView = {
  structure: ReelTranscriptStructure;
  /**
   * Stamps *the caller's* token array with per-word Section ownership. The
   * renderer works on a projection of `getFullTranscript()` whose indices
   * drift one token per section seam, so it must stamp what it actually
   * renders — never reuse an index map computed against another array.
   */
  stampWords: (
    elements: readonly StructureTokenLike[] | undefined,
  ) => ReelWordStamp;
  /**
   * PLAT-11158 — host-owned collapse state and toggles. Optional so a host
   * can publish structure without collapse; absent means everything renders
   * expanded and the headings offer no toggle.
   */
  collapse?: ReelTranscriptCollapseView;
  /**
   * The Sections' `spk_X` labels resolved to the people they were identified
   * as, so two labels for the same person count once in the reel's summary.
   * Host-owned because the fetch belongs to the host's story; absent means the
   * summary counts raw labels, which is what it does before identification.
   */
  speakersByLabel?: ReadonlyMap<string, { id: string }>;
  /**
   * PLAT-11160 — seek playback to a Section's first word. Host-owned so the
   * seek goes through the surface's own creator (the modal's transcript must
   * never move Creator Studio's playhead). Absent = headings don't seek.
   */
  onSectionSeek?: (section: ReelTranscriptSection) => void;
  /**
   * PLAT-11163 — the way back: open the Reel Editor with this Section's row
   * revealed. Host-owned because the host owns the tabs. Absent = the
   * headings offer no way back (a host without a Reel Editor beside it).
   */
  onSectionOpenInEditor?: (section: ReelTranscriptSection) => void;
  /**
   * PLAT-11257 — the two naming actions on a Section heading's own menu: store
   * the name the creator typed (`''` clears it back to the opening words), and
   * name the Section from what is said in it.
   *
   * Host-owned for the reason the seek is: the write goes to the host's own
   * clip variant, and the AI run needs the host's story, prompt and dialog.
   * Absent = the heading offers no menu, which is every host but the clip
   * preview.
   */
  onSectionRename?: (section: ReelTranscriptSection, name: string) => void;
  /**
   * `'auto'` runs the prompt as it stands; `'instruct'` opens the dialog where
   * the creator says what the name should say first. Two ways to ask for the
   * same thing, so a name reached for a second time can be steered rather than
   * re-rolled.
   */
  onSectionGenerateName?: (
    section: ReelTranscriptSection,
    mode: 'auto' | 'instruct',
  ) => void;
  /** Sections with a generation in flight — their menu item stands down. */
  generatingNameSectionIds?: ReadonlySet<string>;
  /**
   * Why the last run on a Section failed, until its next one. The heading says
   * so where the in-flight label was: a run that ends by putting the row back
   * exactly as it was is indistinguishable from one that never started.
   */
  sectionNameErrors?: Readonly<Record<string, string | undefined>>;
};

export const ReelTranscriptStructureContext =
  createContext<ReelTranscriptStructureView | null>(null);

export const useReelTranscriptStructure = () =>
  useContext(ReelTranscriptStructureContext);
